import { createSelector } from 'reselect';
import { formValueSelector } from 'redux-form';
import uniq from 'lodash/uniq';
import { InventoryStocktakeForm } from '../../../../constants/constant_form_names';
import { SERIALIZED, BATCH } from '../../../../constants/constant_item_types';
import {
  PENDING,
  PROCESSED,
} from '../../../../constants/constant_inventory_status';

import {
  PROCESSING,
  CLOSED,
  CLOSED_ABANDONED,
  OPEN,
} from '../../../../constants/constant_transaction_status';
import { getInventoryStatus, getTransactionStatus } from '../../../../common/selectors/selector_common';

const selector = formValueSelector(InventoryStocktakeForm);
const filterSelector = formValueSelector('InventoryStocktakeFormLinesFilter');

function getListDataDocId(state) {
  return (state.listData || {}).docid || {};
}

function getFormIsFetchingItemLocations(state) {
  return selector(state, 'isFetchingItemLocations');
}

function getFormRunStatus(state) {
  return selector(state, 'run_status');
}

function getPropViewMode(state, viewMode) {
  return viewMode;
}

function getFormStatus(state) {
  return selector(state, 'status') || {};
}

function getFormLineByIndex(state, index) {
  return selector(state, `lines[${index}]`) || { item: {} };
}

function getFormItemLines(state) {
  return selector(state, 'lines') || [];
}

function getEnterUnitCost(state) {
  return selector(state, 'enter_unit_cost');
}

function getFormStocktakeType(state) {
  return selector(state, 'stocktake_type') || '';
}

function getFormStartDatetime(state) {
  return selector(state, 'start_datetime') || '';
}

function getFormReferenceId(state) {
  return selector(state, 'reference_id') || '';
}

function getFormLocation(state) {
  return selector(state, 'location_id');
}

function getFilterItems(state) {
  return filterSelector(state, 'item_ids') || [];
}

function getFilterBrands(state) {
  return filterSelector(state, 'brand_ids') || [];
}

function getFilterClassifications(state) {
  return filterSelector(state, 'classification_ids') || [];
}

function getFilterFacets(state) {
  return filterSelector(state, 'facet_ids') || [];
}

function getFilterHideCounted(state) {
  return filterSelector(state, 'hide_counted');
}

function getFilterHidePending(state) {
  return filterSelector(state, 'hide_pending');
}

function getFilterShowConflict(state) {
  return filterSelector(state, 'show_conflict');
}

function getFormMatchedScanItem(state) {
  return selector(state, 'matched_scanned_item');
}

function getStocktakePreference(state) {
  return state.inventory.stocktake.preferences || {};
}

function getFormCurrency(state) {
  return selector(state, 'currency') || {};
}

function getFormId(state) {
  const { _id, id } = selector(state, '_id', 'id');
  return _id || id;
}

export const getMatchedScanItemId = createSelector(
  [getFormMatchedScanItem],
  (item) => {
    return (item || {}).id;
  },
);

export const getMatchedScanItemIndex = createSelector(
  [getFormItemLines, getMatchedScanItemId],
  (lines, itemId) => {
    return lines.findIndex((line) => line.item.id === itemId);
  }
);

export const getFormLines = createSelector(
  [getFormItemLines],
  (lines) => {
    return lines || [];
  },
);

export const getLineItemIds = createSelector(
  [getFormItemLines],
  (formLines) => {
    return formLines.map((formLine) => {
      return (formLine.item || {}).id;
    });
  },
);

export const getLineBrandIds = createSelector(
  [getFormItemLines],
  (formLines) => {
    const brandIds = [];
    formLines.forEach((formLine) => {
      const brandId = (formLine.item || {}).brand_id;
      if (brandId) {
        brandIds.push(brandId);
      }
    });
    return uniq(brandIds);
  },
);

export const getLineClassificationIds = createSelector(
  [getFormItemLines],
  (formLines) => {
    const classificationIds = [];
    formLines.forEach((formLine) => {
      const classificationId = (formLine.item || {}).classification_id;
      if (classificationId) {
        classificationIds.push(classificationId);
      }
    });
    return uniq(classificationIds);
  },
);

export const getLineFacetIds = createSelector(
  [getFormItemLines],
  (formLines) => {
    const facetIds = [];
    formLines.forEach((formLine) => {
      const facetId = (formLine.item || {}).facet_id;
      if (facetId) {
        facetIds.push(facetId);
      }
    });
    return uniq(facetIds);
  },
);

export const getFilteredLines = createSelector(
  [
    getFormItemLines,
    getFilterItems,
    getFilterBrands,
    getFilterClassifications,
    getFilterFacets,
    getFilterHideCounted,
    getFilterHidePending,
    getFilterShowConflict,
  ],
  (
    lines,
    filterItems,
    filterBrands,
    filterClass,
    filterFacets,
    hideCounted,
    hidePending,
    showConflict,
  ) => {
    const filteredLines = lines.filter((line) => {
      const item = line.item || {};
      const itemId = item.id;
      const brandId = item.brand_id;
      const classificationId = item.classification_id;
      const facetId = item.facet_id;

      let selectedItem = true;
      let selectedBrand = true;
      let selectedClass = true;
      let selectedFacet = true;

      if (filterItems.length) {
        selectedItem = filterItems.indexOf(itemId) > -1;
      }

      if (filterBrands.length) {
        selectedBrand = filterBrands.indexOf(brandId) > -1;
      }

      if (filterClass.length) {
        selectedClass = filterClass.indexOf(classificationId) > -1;
      }

      if (filterFacets.length) {
        selectedFacet = filterFacets.indexOf(facetId) > -1;
      }

      if(selectedItem && selectedBrand && selectedClass && selectedFacet) {
        if (hideCounted && line.is_counted) return false;
        if (hidePending && !line.is_counted) return false;
        if (showConflict && line.new_current_quantity) return true;
        if (!showConflict) return true;
        return false; 
      }

      return false;
    });
    return filteredLines;
  },
);

export const hasOnhandConflicts = createSelector(
  [getFormItemLines],
  (lines) => {
    return lines.some((line) => {
      return line.new_current_quantity;
    });
  },
);

export const hasBatchSerializedItems = createSelector(
  [getFormItemLines],
  (lines) => {
    if (!lines.length) return false;
    return lines.some((line) => {
      if (!line) return false;
      const itemType = line.item_type || {};
      return itemType === BATCH || itemType === SERIALIZED;
    });
  },
);

export const hasOnEditModeLine = createSelector(
  [getFormItemLines],
  (lines) => {
    if (!lines.length) return false;
    return lines.some((line) => {
      return (
        line && line.item && !line.quantity && !line.item.id && line.onEditMode
      );
    });
  },
);

export const getEditModeLineIndex = createSelector(
  [getFormItemLines],
  (lines) => {
    if (!lines.length) return null;
    return lines.findIndex((line) => {
      return line && line.onEditMode;
    });
  },
);

export const hasItemWithUnitCost = createSelector(
  [getFormItemLines],
  (lines) => {
    if (!lines.length) return false;
    return lines.some((line) => {
      return line && line.unit_cost;
    });
  },
);

export const isClosed = createSelector(
  [getFormStatus],
  (status) => {
    const statusId = (status || {}).cid;
    return statusId === CLOSED || statusId === CLOSED_ABANDONED;
  },
);

export const isProcessing = createSelector(
  [getFormStatus],
  (status) => {
    const statusId = (status || {}).id;
    return statusId === PROCESSING;
  },
);

export const hasEnterUnitCost = createSelector(
  [getEnterUnitCost],
  (enterUnitCost) => {
    return Boolean(enterUnitCost);
  },
);

export const getUnfilledLineItemIndex = createSelector(
  [getFormItemLines],
  (lines) => {
    return lines.findIndex((line) => {
      return line && !line.quantity && !(line.item || {}).id;
    });
  },
);

export const hasRunStatus = createSelector(
  [getFormRunStatus],
  (runStatus) => {
    return Boolean(runStatus);
  },
);

export const getCounted = createSelector(
  [getFormItemLines],
  (lines) => {
    return lines.reduce((total, line) => {
      if (line.is_counted) return (total += 1);
      return total;
    }, 0);
  },
);

export const getSelected = createSelector(
  [getFormItemLines],
  (lines) => {
    return lines.length;
  },
);

export const getUnfilledLineReferenceIdIndex = createSelector(
  [getFormLineByIndex],
  (itemLine) => {
    return (itemLine.reference_ids || []).findIndex((referenceId) => {
      return referenceId && !referenceId.reference;
    });
  },
);

export const getCurrencyCode = createSelector(
  [getFormCurrency],
  (currency) => {
    return (currency || {}).code || 'USD';
  },
);

export const getEditModeLineReferenceIdIndex = createSelector(
  [getFormLineByIndex],
  (itemLine) => {
    if (!(itemLine.reference_ids || []).length) return null;
    return itemLine.reference_ids.findIndex((itemLineRefId) => {
      return itemLineRefId && itemLineRefId.onRefLineEditMode;
    });
  },
);

export const hasProcessedDate = createSelector(
  [getFormItemLines],
  (lines) => {
    return lines.some((line) => {
      if (!line) return false;
      return Boolean(line.processed_date);
    });
  },
);

export const isFullStocktake = createSelector(
  [getFormStocktakeType],
  (stocktakeType) => {
    return stocktakeType === 'full';
  },
);

export const getStocktakeType = createSelector(
  [getFormStocktakeType],
  (stocktakeType) => stocktakeType,
);

export const getStartDatetime = createSelector(
  [getFormStartDatetime],
  (startDatetime) => startDatetime,
);

export const hasNewCurrentQty = createSelector(
  [getFormItemLines],
  (lines) => {
    return lines.some((line) => {
      return (
        line.new_current_quantity !== null &&
        line.new_current_quantity !== undefined
      );
    });
  },
);

export const getLineNewCurrentQty = createSelector(
  [getFormLineByIndex],
  (line) => line.new_current_quantity,
);
export const getLineNewQty = createSelector(
  [getFormLineByIndex],
  (line) => line.new_quantity,
);
export const getLineCurrentQty = createSelector(
  [getFormLineByIndex],
  (line) => line.current_quantity,
);

export const canLineBeReset = createSelector(
  [getFormLineByIndex, isClosed, isProcessing, hasRunStatus],
  (line, isClosed, isProcessing, hasRunStatus) => {
    if (isClosed || isProcessing || hasRunStatus) return false;
    return line.new_quantity || line.quantity || line.is_counted;
  },
);

export const showIsCounted = createSelector(
  [isClosed, isProcessing, hasRunStatus],
  (isClosed, isProcessing, hasRunStatus) => {
    if (isClosed || isProcessing || hasRunStatus) return false;
    return true;
  },
);

export const getBarcodePreferences = createSelector(
  [getStocktakePreference],
  (preferences) => {
    return preferences || {};
  },
);

export const getIsCounted = createSelector(
  [getFormLineByIndex],
  (line) => {
    return line.is_counted;
  },
);

export const viewModeForm = createSelector(
  [getFormStatus, hasRunStatus, getPropViewMode],
  (status, hasRunStatus, viewMode) => {
    return (
      [PROCESSING, CLOSED, CLOSED_ABANDONED].indexOf(status.cid) > -1 ||
      hasRunStatus ||
      viewMode
    );
  },
);

export const isExistingStocktake = createSelector(
  [getFormId],
  (id) => Boolean(id),
);

export const getLineOnEditMode = createSelector(
  [getFormLineByIndex],
  (line) => Boolean(line.onEditMode),
);

export const hasLineNextStatus = createSelector(
  [getFormLineByIndex],
  (line) => Boolean(line.next_status_id),
);

export const getSystemMessage = createSelector(
  [getFormLineByIndex],
  (line) => line.system_message,
);

export const getLineId = createSelector(
  [getFormLineByIndex],
  (line) => line.line_id,
);

export const getReferenceId = createSelector(
  [getFormReferenceId],
  (referenceId) => referenceId,
);

export const getInventoryStatusPending = createSelector(
  [getListDataDocId],
  (listDataDocId) => {
    const inventoryStatus = listDataDocId[PENDING] || [];
    const pendingStatus = inventoryStatus.length ? inventoryStatus[0] : null;
    if (pendingStatus) {
      const pendingStatusId = pendingStatus.id || pendingStatus._id;
      return { _id: pendingStatusId, name: pendingStatus.name };
    }
    return { _id: PENDING };
  },
);

export const isFetchingItemLocations = createSelector(
  [getFormIsFetchingItemLocations],
  (isFetchingItemLocations) => isFetchingItemLocations,
);

export const getLocationId = createSelector(
  [getFormLocation],
  (location_id) => location_id,
);

export const getLineItemName = createSelector(
  [getFormLineByIndex],
  (line) => (line.item || {}).name,
);

export const getInventoryStatusList = createSelector(
  [getInventoryStatus],
  (statusList) => statusList,
);

export const getInventoryStatusProcessed = createSelector(
  [getInventoryStatus],
  (statusList) => (statusList || []).find((status) => status.cid === PROCESSED),
);

export const getTransactionStatusList = createSelector(
  [getTransactionStatus],
  (statusList) => statusList,
);

export const getTransactionStatusOpen = createSelector(
  [getTransactionStatus],
  (statusList) => (statusList || []).find((status) => status.cid === OPEN),
);

export const getTransactionStatusProcessing = createSelector(
  [getTransactionStatus],
  (statusList) =>
    (statusList || []).find((status) => status.cid === PROCESSING),
);

export const getTransactionStatusClosedAbandoned = createSelector(
  [getTransactionStatus],
  (statusList) =>
    (statusList || []).find((status) => status.cid === CLOSED_ABANDONED),
);

export const isLineBatchSerializedItem = createSelector(
  [getFormLineByIndex],
  (line) => {
    const { item_type } = line;
    return [BATCH, SERIALIZED].indexOf(item_type) > -1;
  },
);

export const isPartQuantity = createSelector(
  [getFormLineByIndex, getStocktakePreference],
  (line, preference) => {
    const { item } = line;
    return (
      (preference || {}).enable_item_part_quantity &&
      (item || {}).enable_part_quantity
    );
  },
);
